import AppStore from '../../stores/AppStore';
import { fetcher } from '../../utility/domUtils';
import templateData from '../../utility/getTemplateData';
import {
  computeAmountAfterFee,
  isOptInCoverFee,
} from '../../components/coverFee/feeUtils';
import * as AppActions from '../../actions/AppActions';
import { trackErrorIM } from '../../fpti/fpti';
import { trackEventWithData } from '../../fpti/analytics';
import { FPTI_TAGS } from '../../fpti/fptiConstants';
import { EVENT_NAME } from '../../utility/constants';

/**
 * Calculates the final donation amount including fee coverage if opted in.
 * If user has opted to cover fees, adds the fee amount to the original donation
 * and backs up the original amount in the store.
 *
 * @returns {number} The calculated donation amount
 */
const getDonationAmount = () => {
  let donationAmount = AppStore.getDonationAmount();

  if (isOptInCoverFee()) {
    const feeAmount = AppStore.getFeeAmount();
    // backup user's original entered amount in store
    AppActions.changeAmountBeforeFee({ amountBeforeFee: donationAmount });

    if (feeAmount) {
      donationAmount = computeAmountAfterFee(donationAmount, feeAmount);
    }
  }

  return donationAmount;
};

/**
 * Builds the parameters object for creating a Venmo order.
 * Collects all necessary donation information from the app store and template data.
 *
 * @returns {Object} Parameters object containing all order details
 */
const buildParams = () => ({
  amount: getDonationAmount(),
  addressSharingConsent: false,
  currencyCode: AppStore.getDonationCode(),
  currencySymbol: AppStore.getDonationSymbol(),
  donationAmountType:
    AppStore.getDonationAmountType() === 'preset' ? 'preset' : 'userinput',
  donationNote: AppStore.getDonationNote() || '',
  recurring: AppStore.getDonationRecurring(),
  selectedProgram: AppStore.getSelectedProgram(),
  selectedPresetButtonId: AppStore.getSelectedPresetButtonId(),
  token: templateData.token,
  anonymousStatus: AppStore.getAnonymousStatus(),
  hasCoverFeeOptedIn: AppStore.getCoverFeeOptInStatus() === 'checked',
});

/**
 * Initiates the order creation process by making an API call to create a Venmo order.
 * Handles the API response, tracks success events, and validates the order ID.
 *
 * @param {Object} params - Order parameters containing donation details
 * @returns {Promise<string>} The created order ID
 * @throws {Error} If no order ID is received or API returns cartUrl instead
 */
const initiateOrder = async (params) => {
  const response = await fetcher(
    `/donate/venmo/orders?dtoken=${templateData.token}`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    }
  );

  const result = await response.json();

  if (result?.data?.order?.id) {
    trackEventWithData({
      event_name: EVENT_NAME.DONATE_VENMO_CREATE_ORDER_SUCCEEDED,
      data: {
        [FPTI_TAGS.ORDER_ID]: result?.data?.order?.id,
        [FPTI_TAGS.TOKEN]: templateData.token,
      },
    });
    return result.data.order.id;
  }

  if (result.cartUrl) {
    throw new Error('API returned cartUrl instead of orderID');
  }

  throw new Error('No order ID received from API');
};

/**
 * Main function to create a Venmo payment order.
 * This function orchestrates the entire order creation process including:
 * - Building order parameters from app state
 * - Initiating the order creation API call
 * - Handling errors with analytics tracking and user alerts
 *
 * @param {Function} handleAlert - Callback function to display alert messages to the user
 * @returns {Promise<string>} The created order ID
 * @throws {Error} If order creation fails
 */
export const createOrder = async (handleAlert) => {
  const orderParams = buildParams();
  try {
    return await initiateOrder(orderParams);
  } catch (error) {
    trackErrorIM({
      errorCode: error?.status || 'donate_venmo_create_order_failed',
      errorMessage: error?.message || 'Venmo create order failed.',
      fieldName: 'Venmo Create Order',
      additionalData: {
        [FPTI_TAGS.EVENT_NAME]: EVENT_NAME.DONATE_VENMO_CREATE_ORDER_FAILED,
        [FPTI_TAGS.TOKEN]: templateData.token,
      },
    });
    handleAlert(true, {
      type: 'error',
      messageId: 'errors.500.error.message',
    });
    throw error;
  }
};

export default createOrder;
